import { useState } from "react";
import {
  Megaphone,
  CalendarDays,
  MapPin,
  Eye,
  FileText,
  Briefcase,
  History,
  UserPlus,
  UserMinus,
  LogIn,
  Building2,
  ChevronRight,
  Clock,
  Star,
  Layers,
} from "lucide-react";

/* ─── STATO NON LOGGATO ─── */
function NotLoggedIn({ onLogin }) {
  return (
    <div className="mt-12 relative overflow-hidden rounded-3xl border border-[hsl(var(--border-soft))] bg-gradient-to-br from-[hsl(var(--bg-2)/0.9)] to-[hsl(var(--bg-1)/0.95)] p-12 text-center">
      {/* Glow decorativo */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-64 w-64 rounded-full bg-amber-500/8 blur-3xl" />
      </div>
      <div className="relative">
        <span className="inline-flex w-20 h-20 rounded-2xl items-center justify-center bg-gradient-to-br from-amber-400/20 to-amber-600/10 border border-amber-500/25 mb-6">
          <Briefcase className="w-9 h-9 text-amber-400" strokeWidth={1.4} />
        </span>
        <p className="text-2xl font-semibold text-white">Accesso richiesto</p>
        <p className="text-sm mt-3 text-[hsl(var(--text-soft))] max-w-sm mx-auto leading-relaxed">
          Effettua il login per visualizzare la tua vita lavorativa, i concorsi e lo storico impieghi.
        </p>
        {onLogin && (
          <button onClick={onLogin} className="btn-brand inline-flex items-center gap-2 mt-7">
            <LogIn className="w-4 h-4" /> Accedi adesso
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── CARD CONCORSO — orizzontale con numero d'ordine ─── */
function ConcorsoCard({ concorso, index }) {
  return (
    <div className="group relative flex items-center gap-5 p-5 rounded-2xl bg-[hsl(var(--bg-3)/0.5)] border border-[hsl(var(--border-soft))] hover:border-amber-500/40 hover:bg-[hsl(var(--bg-3)/0.8)] transition-all duration-200">
      {/* Numero d'ordine */}
      <span className="shrink-0 w-8 h-8 rounded-full border border-[hsl(var(--border-strong))] flex items-center justify-center text-[11px] font-bold text-[hsl(var(--text-mute))] group-hover:border-amber-500/50 group-hover:text-amber-400 transition-colors">
        {String(index + 1).padStart(2, "0")}
      </span>

      {/* Logo / placeholder */}
      {concorso.immagine ? (
        <img
          src={concorso.immagine}
          alt={concorso.nome}
          className="shrink-0 w-12 h-12 rounded-xl object-cover border border-[hsl(var(--border-soft))]"
        />
      ) : (
        <span className="shrink-0 inline-flex w-12 h-12 rounded-xl items-center justify-center bg-[hsl(var(--bg-2))] border border-[hsl(var(--border-soft))]">
          <Building2 className="w-5 h-5 text-[hsl(var(--text-mute))]" />
        </span>
      )}

      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white leading-snug">{concorso.nome}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
          <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--text-mute))]">
            <CalendarDays className="w-3 h-3 text-amber-400/70" />
            {concorso.data}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--text-mute))]">
            <MapPin className="w-3 h-3 text-amber-400/70" />
            {concorso.luogo}
          </span>
        </div>
      </div>

      <button className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 hover:bg-amber-500/20 hover:border-amber-500/50 transition text-[11px] font-semibold uppercase tracking-wider">
        <Eye className="w-3.5 h-3.5" />
        Leggi
      </button>
    </div>
  );
}

/* ─── BADGE STATO CANDIDATURA ─── */
function StatoBadge({ stato }) {
  const map = {
    accettata: "bg-emerald-500/12 text-emerald-300 border-emerald-500/30",
    rifiutata: "bg-red-500/12 text-red-300 border-red-500/30",
  };
  return (
    <span className={`text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-lg border uppercase whitespace-nowrap ${map[stato] || "bg-amber-500/12 text-amber-300 border-amber-500/30"}`}>
      {stato}
    </span>
  );
}

/* ─── CARD LAVORO ATTUALE — con accent bar laterale ─── */
function LavoroAttualeCard({ lavoro }) {
  return (
    <div className="relative flex items-center gap-4 p-4 pl-5 rounded-2xl bg-[hsl(var(--bg-3)/0.5)] border border-[hsl(var(--border-soft))] hover:border-emerald-500/35 hover:bg-[hsl(var(--bg-3)/0.8)] transition-all duration-200 overflow-hidden">
      {/* Barra laterale accent */}
      <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600/40" />

      {lavoro.icona ? (
        <img
          src={lavoro.icona}
          alt={lavoro.azienda}
          className="shrink-0 w-11 h-11 rounded-xl object-cover border border-[hsl(var(--border-soft))]"
        />
      ) : (
        <span className="shrink-0 inline-flex w-11 h-11 rounded-xl items-center justify-center bg-[hsl(var(--bg-2))] border border-[hsl(var(--border-soft))]">
          <Building2 className="w-5 h-5 text-[hsl(var(--text-mute))]" />
        </span>
      )}

      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white leading-snug truncate">{lavoro.azienda}</div>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-[hsl(var(--text-mute))]">
          <span className="truncate">{lavoro.ruolo}</span>
          <span className="shrink-0">·</span>
          <span className="shrink-0 inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Dal {lavoro.dataInizio}
          </span>
        </div>
      </div>

      {lavoro.badge && (
        <span className="shrink-0 text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-lg bg-emerald-500/12 text-emerald-300 border border-emerald-500/30 uppercase">
          {lavoro.badge}
        </span>
      )}
    </div>
  );
}

/* ─── CARD STORICO — timeline style ─── */
function StoricoCard({ record, isLast }) {
  return (
    <div className="relative flex gap-4">
      {/* Linea timeline */}
      {!isLast && (
        <span className="absolute left-[19px] top-10 bottom-0 w-px bg-[hsl(var(--border-soft))]" />
      )}

      {/* Dot */}
      <span className="shrink-0 mt-1 w-10 h-10 rounded-full bg-[hsl(var(--bg-3))] border border-[hsl(var(--border-strong))] flex items-center justify-center z-10">
        <Briefcase className="w-4 h-4 text-[hsl(var(--text-mute))]" />
      </span>

      {/* Contenuto */}
      <div className="flex-1 pb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-white leading-snug">{record.azienda}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--text-mute))] mt-0.5">{record.tipo}</div>
          </div>
          <span className="text-[11px] text-[hsl(var(--text-soft))] bg-[hsl(var(--bg-3))] border border-[hsl(var(--border-soft))] rounded-lg px-2.5 py-1 whitespace-nowrap">
            {record.periodo}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-[11px]">
          <span className="inline-flex items-center gap-1.5 text-emerald-400">
            <UserPlus className="w-3.5 h-3.5" />
            Assunto da <span className="font-semibold text-white ml-0.5">{record.assuntoDa}</span>
          </span>
          {record.licenziatoDa && (
            <span className="inline-flex items-center gap-1.5 text-red-400">
              <UserMinus className="w-3.5 h-3.5" />
              Licenziato da <span className="font-semibold text-white ml-0.5">{record.licenziatoDa}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── COMPONENTE PRINCIPALE ─── */
export default function VitaLavorativa({ stats, user, onLogin }) {
  const [concorsiTab, setConcorsiTab] = useState("bandi"); // bandi | candidature
  const isLogged = !!user;

  const concorsi = stats?.concorsi || [];
  const candidature = stats?.candidature || [];
  const lavoriAttuali = stats?.lavoriAttuali || [];
  const storicoLavorativo = stats?.storicoLavorativo || [];

  return (
    <section className="mt-20" data-testid="section-lavoro">

      {/* ── Header ── */}
      <div className="text-center fade-up">
        <h2 className="font-display title-stroke text-5xl sm:text-7xl tracking-wider leading-none">
          Vita Lavorativa
        </h2>
        <p className="mt-4 text-[10px] sm:text-xs uppercase tracking-[0.45em] text-[hsl(var(--text-mute))]">
          Panoramica dei concorsi e carriera lavorativa
        </p>
        <div className="lavoro-divider mt-7" />
      </div>

      {!isLogged && <NotLoggedIn onLogin={onLogin} />}

      {isLogged && (
        <div className="mt-12 space-y-10">

          {/* ══════════════════════════════════════
              BLOCCO CONCORSI — layout a colonna unica
              con tab switcher integrato nell'header
          ══════════════════════════════════════ */}
          <div className="rounded-3xl border border-[hsl(var(--border-soft))] bg-gradient-to-b from-[hsl(38_92%_50%/0.04)] to-transparent overflow-hidden">

            {/* Header blocco */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 pt-6 pb-5 border-b border-[hsl(var(--border-soft))]">
              <div className="flex items-center gap-4">
                <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center bg-gradient-to-br from-amber-400 via-amber-500 to-amber-700 shadow-lg shadow-amber-500/30">
                  <Megaphone className="w-6 h-6 text-white" />
                </span>
                <div>
                  <h3 className="font-display text-2xl sm:text-3xl tracking-wide text-white leading-none">Area Concorsi</h3>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-mute))] mt-1.5">
                    Bandi attivi e candidature
                  </p>
                </div>
              </div>

              {/* Tab switcher */}
              <div className="seg-wrap self-start sm:self-auto">
                <button
                  onClick={() => setConcorsiTab("bandi")}
                  className={`seg-pill ${concorsiTab === "bandi" ? "seg-pill-active" : ""}`}
                >
                  <CalendarDays className="w-3.5 h-3.5" /> Bandi
                </button>
                <button
                  onClick={() => setConcorsiTab("candidature")}
                  className={`seg-pill ${concorsiTab === "candidature" ? "seg-pill-active" : ""}`}
                >
                  <FileText className="w-3.5 h-3.5" /> Candidature
                </button>
              </div>
            </div>

            {/* Contenuto tab */}
            <div className="p-6">
              {concorsiTab === "bandi" && (
                <>
                  {concorsi.length > 0 ? (
                    <div className="space-y-3">
                      {concorsi.map((c, i) => <ConcorsoCard key={i} concorso={c} index={i} />)}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center py-14">
                      <CalendarDays className="w-10 h-10 mb-3 text-[hsl(var(--text-mute))]" strokeWidth={1.3} />
                      <p className="text-sm font-medium text-[hsl(var(--text-soft))]">Nessun concorso in programma</p>
                      <p className="text-xs text-[hsl(var(--text-mute))] mt-1">I bandi attivi appariranno qui</p>
                    </div>
                  )}
                </>
              )}

              {concorsiTab === "candidature" && (
                <>
                  {candidature.length > 0 ? (
                    <div className="space-y-2">
                      {candidature.map((c, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-[hsl(var(--bg-3)/0.5)] border border-[hsl(var(--border-soft))] hover:border-[hsl(var(--border-strong))] transition">
                          <span className="shrink-0 inline-flex w-9 h-9 rounded-xl items-center justify-center bg-[hsl(var(--bg-2))] border border-[hsl(var(--border-soft))]">
                            <FileText className="w-4 h-4 text-[hsl(var(--text-mute))]" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">{c.posizione}</div>
                            <div className="text-[11px] text-[hsl(var(--text-mute))] mt-0.5">{c.data}</div>
                          </div>
                          <StatoBadge stato={c.stato} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center py-14">
                      <FileText className="w-10 h-10 mb-3 text-[hsl(var(--text-mute))]" strokeWidth={1.3} />
                      <p className="text-sm font-medium text-[hsl(var(--text-soft))]">Nessuna candidatura attiva</p>
                      <p className="text-xs text-[hsl(var(--text-mute))] mt-1">Non hai sottoscritto nessuna lista di recente</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ══════════════════════════════════════
              BLOCCO LAVORATIVO — due colonne
          ══════════════════════════════════════ */}
          <div className="rounded-3xl border border-[hsl(var(--border-soft))] bg-gradient-to-b from-[hsl(145_75%_50%/0.04)] to-transparent overflow-hidden">

            {/* Header blocco */}
            <div className="flex items-center gap-4 px-6 pt-6 pb-5 border-b border-[hsl(var(--border-soft))]">
              <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-700 shadow-lg shadow-emerald-500/30">
                <Briefcase className="w-6 h-6 text-white" />
              </span>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl tracking-wide text-white leading-none">Area Lavorativa</h3>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-mute))] mt-1.5">
                  I tuoi impieghi attuali e passati
                </p>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Lavori attuali */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">Impieghi attivi</span>
                </div>
                {lavoriAttuali.length > 0 ? (
                  <div className="space-y-3">
                    {lavoriAttuali.map((l, i) => <LavoroAttualeCard key={i} lavoro={l} />)}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-12 rounded-2xl bg-[hsl(var(--bg-3)/0.3)] border border-dashed border-[hsl(var(--border-soft))]">
                    <Briefcase className="w-9 h-9 mb-3 text-[hsl(var(--text-mute))]" strokeWidth={1.3} />
                    <p className="text-sm text-[hsl(var(--text-mute))]">Nessun impiego attivo</p>
                  </div>
                )}
              </div>

              {/* Storico lavorativo — timeline */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Layers className="w-3.5 h-3.5 text-sky-400" />
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">Storico lavorativo</span>
                </div>
                {storicoLavorativo.length > 0 ? (
                  <div className="max-h-[420px] overflow-y-auto pr-1 custom-scroll">
                    {storicoLavorativo.map((r, i) => (
                      <StoricoCard
                        key={i}
                        record={r}
                        isLast={i === storicoLavorativo.length - 1}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-12 rounded-2xl bg-[hsl(var(--bg-3)/0.3)] border border-dashed border-[hsl(var(--border-soft))]">
                    <History className="w-9 h-9 mb-3 text-[hsl(var(--text-mute))]" strokeWidth={1.3} />
                    <p className="text-sm text-[hsl(var(--text-mute))]">Nessuno storico disponibile</p>
                  </div>
                )}
              </div>

            </div>
          </div>

        </div>
      )}
    </section>
  );
}
